/**
 * "Use for a Client…" from Firm Knowledge. An intake writes to a client's
 * record, so it starts with picking one: search the firm's clients, then see
 * what they get (the note with their name filled in, the video, the size,
 * the link and reminders, all from the template) and the pre-send checks.
 * Send emails the link; Open Draft in Intake Requests stops at the draft so any of
 * it can change first. Neither changes the template.
 */
import {
  AlertCircle,
  AlertTriangle,
  Bell,
  CalendarClock,
  CheckCircle2,
  FileText,
  Loader2,
  Search,
  Send,
  Video
} from "lucide-react";
import {
  useEffect,
  useMemo,
  useState,
  type ComponentType,
  type FC,
  type ReactNode
} from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";

import { OutgoingIntakesAPI } from "@/api/outgoingIntakesApi";
import type {
  OutgoingIntakeFormNode,
  OutgoingIntakePresendIssue,
  OutgoingIntakeSavedTemplate
} from "@/api/types/outgoingIntake";
import { APIError } from "@/api/utils/errors";
import { Avatar, AvatarFallback } from "@/components/ui/avatar";
import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle
} from "@/components/ui/dialog";
import { Field, FieldDescription, FieldLabel } from "@/components/ui/field";
import { Input } from "@/components/ui/input";
import { NavRow } from "@/components/ui/nav-row";
import { Spinner } from "@/components/ui/spinner";
import { useClientSearch } from "@/features/newCase/hooks/useClientSearch";
import type { SelectedClient } from "@/features/newCase/types";
import { clientInitials } from "@/features/newCase/utils";
import {
  errorCount,
  presendChecklist,
  withServerIssues,
  type PresendItem
} from "@/features/requestsIde/editor/presend";
import { cadenceLabel } from "@/features/requestsIde/editor/reminders";
import {
  DEFAULT_EXPIRY_DAYS,
  DEFAULT_REMINDERS,
  isEmail
} from "@/features/requestsIde/editor/useIntakeDraft";
import { firstName } from "@/features/requestsIde/editor/useSubmissionReview";
import { intakePath } from "@/features/requestsIde/routes";
import { cn } from "@/lib/utils";

import { fillNote, sizeOf, templateReminders } from "../intakeTemplates";

export type UsableTemplate = {
  id: string;
  name: string;
  /** null while a starter's questions are still loading. */
  schema: OutgoingIntakeFormNode[] | null;
  /** A firm template's defaults; a starter has none. */
  defaults?: Pick<
    OutgoingIntakeSavedTemplate,
    | "attorney_note"
    | "expires_in_days"
    | "reminder_schedule"
    | "welcome_video_url"
  > | null;
};

type Props = {
  template: UsableTemplate | null;
  onOpenChange: (open: boolean) => void;
  /** Save the editor's pending edits first. */
  beforeSend?: () => Promise<void>;
  /** An intake went out (or was created as a draft); counts may change. */
  onDone?: () => void;
};

const CheckIcon: FC<{ severity: PresendItem["severity"] }> = ({ severity }) =>
  severity === "ok" ? (
    <CheckCircle2 className="size-3.5 shrink-0 text-success" />
  ) : severity === "warning" ? (
    <AlertTriangle className="size-3.5 shrink-0 text-warn" />
  ) : (
    <AlertCircle className="size-3.5 shrink-0 text-danger" />
  );

const serverIssuesOf = (e: unknown): OutgoingIntakePresendIssue[] => {
  if (!(e instanceof APIError)) return [];
  const details = e.details as
    | { code?: string; issues?: OutgoingIntakePresendIssue[] }
    | undefined;
  return details?.code === "presend_failed" && Array.isArray(details.issues)
    ? details.issues
    : [];
};

const GetsLine: FC<{
  icon: ComponentType<{ className?: string }>;
  muted?: boolean;
  children: ReactNode;
}> = ({ icon: Icon, muted, children }) => (
  <span
    className={cn(
      "flex items-start gap-2 text-base",
      muted ? "text-fg-3" : "text-fg-2"
    )}
  >
    <Icon className="mt-0.5 size-3.5 shrink-0 text-fg-3" />
    <span className="min-w-0">{children}</span>
  </span>
);

export const UseTemplateDialog: FC<Props> = ({
  template,
  onOpenChange,
  beforeSend,
  onDone
}) => {
  const navigate = useNavigate();
  const [query, setQuery] = useState("");
  const [client, setClient] = useState<SelectedClient | null>(null);
  const [email, setEmail] = useState("");
  const [busy, setBusy] = useState<"send" | "open" | null>(null);
  const [serverIssues, setServerIssues] = useState<
    OutgoingIntakePresendIssue[]
  >([]);
  const search = useClientSearch(template ? query : "");

  useEffect(() => {
    if (!template) return;
    setQuery("");
    setClient(null);
    setEmail("");
    setServerIssues([]);
  }, [template]);

  const pick = (next: SelectedClient) => {
    setClient(next);
    setEmail(next.email);
    setServerIssues([]);
  };

  const items = useMemo(
    () =>
      client && template?.schema
        ? withServerIssues(
            presendChecklist(template.schema, email),
            serverIssues
          )
        : [],
    [client, email, serverIssues, template]
  );
  const errors = errorCount(items);
  const first = client ? firstName(client.name) ?? client.name : "";
  const defaults = template?.defaults ?? null;
  const note = defaults?.attorney_note?.trim()
    ? fillNote(defaults.attorney_note.trim(), first)
    : null;
  const reminders = cadenceLabel(
    templateReminders(defaults?.reminder_schedule, DEFAULT_REMINDERS)
  );

  const submit = async (send: boolean) => {
    if (!template || !client) return;
    setBusy(send ? "send" : "open");
    try {
      await beforeSend?.();
      const result = await OutgoingIntakesAPI.sendSavedTemplate(template.id, {
        foreign_national_id: client.id,
        email: isEmail(email) ? email.trim() : null,
        send
      });
      onDone?.();
      onOpenChange(false);
      const path = intakePath(
        client.id,
        result.outgoing_intake.outgoing_intake_id
      );
      if (!send) {
        toast.success(
          `Created “${template.name}” in ${client.name}'s Intake Requests`
        );
        navigate(path);
        return;
      }
      const delivered = result.email_delivered !== false;
      (delivered ? toast.success : toast.warning)(
        delivered
          ? `Sent to ${client.name}. You'll see when it's delivered and opened.`
          : `Created and sent, but the email to ${client.name} didn't go out. Copy the link from Intake Requests.`,
        {
          action: {
            label: "View in Intake Requests",
            onClick: () => navigate(path)
          }
        }
      );
    } catch (e) {
      const issues = serverIssuesOf(e);
      if (issues.length) setServerIssues(issues);
      toast.error(
        e instanceof Error ? e.message : "Couldn't send the template"
      );
    } finally {
      setBusy(null);
    }
  };

  return (
    <Dialog open={template !== null} onOpenChange={onOpenChange}>
      <DialogContent
        className="grid-cols-1 sm:max-w-lg"
        data-testid="use-template-dialog"
      >
        <DialogHeader>
          <DialogTitle>Use “{template?.name}” for a Client</DialogTitle>
          <DialogDescription>
            Makes an intake from this template in the client&apos;s Intake
            Requests. Changes there don&apos;t touch the template.
          </DialogDescription>
        </DialogHeader>

        <div className="flex max-h-[60vh] flex-col gap-4 overflow-y-auto">
          <Field>
            <FieldLabel>Client</FieldLabel>
            {client ? (
              <div className="flex h-10 items-center gap-2.5 rounded-md px-2 ring-1 ring-line-3">
                <Avatar size="sm">
                  <AvatarFallback>{clientInitials(client)}</AvatarFallback>
                </Avatar>
                <span className="min-w-0 flex-1 truncate text-base text-fg">
                  {client.name}
                </span>
                <Button
                  variant="ghost"
                  size="xs"
                  onClick={() => setClient(null)}
                  data-testid="use-change-client"
                >
                  Change
                </Button>
              </div>
            ) : (
              <div className="flex flex-col gap-1">
                <div className="relative">
                  <Search
                    className="pointer-events-none absolute top-1/2 left-2.5 size-3.5 -translate-y-1/2 text-fg-3"
                    aria-hidden
                  />
                  <Input
                    autoFocus
                    value={query}
                    onChange={(e) => setQuery(e.target.value)}
                    placeholder="Search clients…"
                    aria-label="Search Clients"
                    className="pl-8"
                    data-testid="use-client-search"
                  />
                </div>
                <div
                  className="flex max-h-48 flex-col overflow-y-auto"
                  role="listbox"
                  aria-label="Clients"
                >
                  {search.isLoading ? (
                    <div className="flex items-center gap-2 px-2 py-2 text-sm text-fg-3">
                      <Spinner className="size-3.5" /> Finding clients…
                    </div>
                  ) : search.clients.length === 0 ? (
                    <p className="px-2 py-2 text-sm text-fg-3">
                      {search.error ?? "No client matches."}
                    </p>
                  ) : (
                    search.clients.slice(0, 8).map((c) => (
                      <NavRow
                        key={c.id}
                        role="option"
                        aria-selected={false}
                        onClick={() => pick(c)}
                        className="h-10"
                        data-testid="use-client-option"
                      >
                        <span className="flex min-w-0 items-center gap-2.5">
                          <Avatar size="sm">
                            <AvatarFallback>{clientInitials(c)}</AvatarFallback>
                          </Avatar>
                          <span className="flex min-w-0 flex-col">
                            <span className="truncate text-fg">{c.name}</span>
                            {c.email ? (
                              <span className="truncate text-sm text-fg-3">
                                {c.email}
                              </span>
                            ) : null}
                          </span>
                        </span>
                      </NavRow>
                    ))
                  )}
                </div>
              </div>
            )}
          </Field>

          {client ? (
            <>
              <Field>
                <FieldLabel htmlFor="use-template-email">Send To</FieldLabel>
                <Input
                  id="use-template-email"
                  type="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                />
                {client.email && email.trim() === client.email ? (
                  <FieldDescription>
                    From the client&apos;s profile.
                  </FieldDescription>
                ) : null}
              </Field>

              <div
                className="flex flex-col gap-2 rounded-xl p-3 ring-1 ring-line-2"
                data-testid="use-template-gets"
              >
                <span className="text-sm font-medium text-fg-2">
                  What {first} Gets
                </span>
                {note ? (
                  <p className="rounded-lg bg-fill-1 px-3 py-2 text-base whitespace-pre-line text-fg">
                    {note}
                  </p>
                ) : null}
                <GetsLine icon={FileText}>
                  {template?.schema ? sizeOf(template.schema) : "Loading…"}
                </GetsLine>
                <GetsLine icon={Video} muted={!defaults?.welcome_video_url}>
                  {defaults?.welcome_video_url
                    ? "Your welcome video first"
                    : "No welcome video"}
                </GetsLine>
                <GetsLine icon={CalendarClock}>
                  Link lasts {defaults?.expires_in_days ?? DEFAULT_EXPIRY_DAYS}{" "}
                  days
                </GetsLine>
                <GetsLine icon={Bell} muted={reminders === "Off"}>
                  {reminders === "Off"
                    ? "No reminders"
                    : `Reminders: ${reminders}`}
                </GetsLine>
                <span className="text-sm text-fg-3">
                  To change any of this for {first}, open the draft first.
                </span>
              </div>

              <div
                className="flex flex-col gap-1"
                data-testid="use-template-checks"
              >
                <div className="flex items-center justify-between">
                  <span className="text-sm font-medium text-fg-2">
                    Before You Send
                  </span>
                  {!template?.schema ? (
                    <Spinner className="size-3.5" />
                  ) : errors ? (
                    <span className="text-sm text-danger">{errors} to fix</span>
                  ) : (
                    <span className="text-sm text-success">Ready</span>
                  )}
                </div>
                {items
                  .filter((c) => c.severity !== "ok")
                  .map((c) => (
                    <div
                      key={`${c.code}|${c.nodeId ?? ""}|${c.message}`}
                      className="flex min-h-6 items-center gap-2 text-sm"
                    >
                      <CheckIcon severity={c.severity} />
                      <span className="min-w-0 flex-1 text-fg">
                        {c.message}
                      </span>
                    </div>
                  ))}
              </div>
            </>
          ) : null}
        </div>

        <DialogFooter className="items-center sm:justify-between">
          <span className="truncate text-sm text-fg-3">
            {client
              ? `Lands in ${client.name}'s Intake Requests`
              : "Pick a client"}
          </span>
          <div className="flex gap-2">
            <Button
              variant="secondary"
              size="sm"
              disabled={!client || busy !== null}
              onClick={() => void submit(false)}
              data-testid="use-template-open-draft"
            >
              {busy === "open" ? <Loader2 className="animate-spin" /> : null}
              Open Draft in Intake Requests
            </Button>
            <Button
              variant="primary"
              size="sm"
              disabled={
                !client || busy !== null || !template?.schema || errors > 0
              }
              onClick={() => void submit(true)}
              data-testid="use-template-send"
            >
              {busy === "send" ? (
                <Loader2 className="animate-spin" />
              ) : (
                <Send />
              )}
              {client ? `Send to ${first}` : "Send"}
            </Button>
          </div>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};
